import React, { useContext, useMemo } from "react";
import {
  Trash2, Bell, Plane, CalendarDays, FileText, ListChecks, MessageSquare,
  HandCoins, UserCircle, UserPlus, BarChart3, AlertTriangle, ClipboardCheck,
  Banknote, Clock, ShieldCheck, Building2, PartyPopper, Users, CheckCircle2,
  XCircle, Zap, User,
} from "lucide-react";
import { useNotifications } from "../../context/NotificationContext";
import { LanguageContext } from "../../constant/LanguageContext";

// Latin digits in both languages, matching every other number in the app
// (plain "ar"/"ar-EG" would switch times to Eastern Arabic digits).
const localeFor = (isAr) => (isAr ? "ar-u-nu-latn" : "en-US");

// A row that arrived in real time is highlighted for this long.
const LIVE_HIGHLIGHT_MS = 5000;

// Notification type -> icon, accent color and label. Order matters: the first
// matching rule wins (e.g. LEAVE_APPROVED reads as a Leave notification). The
// icon carries the type visually; the label is only its tooltip, so the type
// is never spelled out next to a title that already says it.
const TYPE_RULES = [
  // Superadmin only: a client company added / imported data (dataEntryAlertService).
  { test: (t) => t.includes("CLIENT_DATA_ENTRY"), Icon: Building2, color: "#0ea5e9", en: "Client activity", ar: "نشاط العملاء" },
  // Superadmin only: a suspicious remote punch (remotePunchService).
  { test: (t) => t.includes("REMOTE_PUNCH"), Icon: AlertTriangle, color: "#dc2626", en: "Remote punch", ar: "بصمة عن بُعد" },
  { test: (t) => t.includes("LEAVE"), Icon: Plane, color: "#8b5cf6", en: "Leave", ar: "إجازة" },
  { test: (t) => t.includes("DOCUMENT"), Icon: FileText, color: "#f59e0b", en: "Document", ar: "مستند" },
  { test: (t, n) => t.includes("CALENDAR") || !!n.eventId, Icon: CalendarDays, color: "#3b82f6", en: "Calendar", ar: "تقويم" },
  { test: (t) => t.includes("TASK"), Icon: ListChecks, color: "#14b8a6", en: "Task", ar: "مهمة" },
  { test: (t) => t.includes("MESSAGE"), Icon: MessageSquare, color: "#06b6d4", en: "Message", ar: "رسالة" },
  { test: (t) => t.includes("ADVANCEMENT"), Icon: HandCoins, color: "#10b981", en: "Advancement", ar: "سلفة" },
  { test: (t) => t.includes("PROFILE"), Icon: UserCircle, color: "#ec4899", en: "Profile", ar: "الملف الشخصي" },
  { test: (t) => t.includes("EMPLOYEE"), Icon: UserPlus, color: "#ec4899", en: "Employee", ar: "موظف" },
  { test: (t) => t.includes("REPORT"), Icon: BarChart3, color: "#0891b2", en: "Report", ar: "تقرير" },
  { test: (t) => t.includes("ESCALATION"), Icon: AlertTriangle, color: "#f43f5e", en: "Complaint", ar: "شكوى" },
  { test: (t) => t.includes("WORKFLOW"), Icon: ClipboardCheck, color: "#f59e0b", en: "Approval", ar: "موافقة" },
  { test: (t) => t.includes("PAYROLL"), Icon: Banknote, color: "#10b981", en: "Payroll", ar: "الرواتب" },
  { test: (t) => t.includes("ATTENDANCE"), Icon: Clock, color: "#f97316", en: "Attendance", ar: "الحضور" },
  { test: (t) => t.includes("PASSWORD") || t.includes("PERMISSION") || t.includes("ADMIN_LEVEL"), Icon: ShieldCheck, color: "#64748b", en: "Account", ar: "الحساب" },
  { test: (t) => t.includes("CONFIG"), Icon: Building2, color: "#3b82f6", en: "Company", ar: "الشركة" },
  { test: (t) => t.includes("WELCOME"), Icon: PartyPopper, color: "#10b981", en: "Welcome", ar: "مرحبًا" },
  { test: (t) => t.includes("GROUP"), Icon: Users, color: "#6366f1", en: "Group", ar: "مجموعة" },
  { test: (t) => t.includes("APPROVED") || t.includes("SUCCESS"), Icon: CheckCircle2, color: "#10b981" },
  { test: (t) => t.includes("REJECTED") || t.includes("ERROR"), Icon: XCircle, color: "#ef4444" },
  { test: (t) => t.includes("ACTION"), Icon: Zap, color: "#f97316" },
];
const DEFAULT_TYPE = { Icon: Bell, color: "#6366f1" };

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * ONE short time label per notification — relative within the last week
 * ("Just now", "5 min. ago", "Yesterday"), a date after that. The exact
 * date-time is the label's tooltip, so time is never printed twice.
 */
function formatWhen(date, isAr) {
  const locale = localeFor(isAr);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return isAr ? "الآن" : "Just now";
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days === 0) return rtf.format(-Math.floor(minutes / 60), "hour");
  if (days < 7) return rtf.format(-days, "day");
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
}

const NotificationItem = ({ notification, onClick }) => {
  const { markAsRead, deleteNotification } = useNotifications();
  const { language } = useContext(LanguageContext);
  const isAr = language === "ar";

  const handleClick = (e) => {
    e.stopPropagation();
    if (!notification.read) {
      markAsRead(notification.id);
    }
    onClick();
  };

  const handleKeyDown = (e) => {
    // Only the row itself — Enter on the nested delete button must not open it.
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick(e);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    deleteNotification(notification.id);
  };

  const date = useMemo(() => {
    const d = new Date(notification.createdAt);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [notification.createdAt]);

  const exactDateTime = useMemo(
    () => (date
      ? new Intl.DateTimeFormat(localeFor(isAr), { dateStyle: "medium", timeStyle: "short" }).format(date)
      : ""),
    [date, isAr]
  );

  const typeMeta = useMemo(() => {
    const type = notification.type || "";
    return TYPE_RULES.find((rule) => rule.test(type, notification)) || DEFAULT_TYPE;
  }, [notification]);

  const { Icon, color } = typeMeta;
  const typeLabel = isAr ? typeMeta.ar : typeMeta.en;
  const unread = !notification.read;
  const title = notification.title || notification.message || "";
  const showMessage = notification.message && notification.message !== title;
  // Computed on every render — the dropdown re-renders on a clock — so the
  // label moves on from "Just now" by itself.
  const timeLabel = date ? formatWhen(date, isAr) : "";
  const isLive = !!notification._liveAt && Date.now() - notification._liveAt < LIVE_HIGHLIGHT_MS;
  const deleteLabel = isAr ? "حذف الإشعار" : "Delete notification";

  return (
    <div
      className={`notification-item${unread ? " unread" : ""}${isLive ? " is-live" : ""}${notification.isPersonal ? " is-personal" : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div
        className="notification-avatar"
        style={{ color, background: `${color}1f` }}
        title={typeLabel || undefined}
      >
        <Icon size={17} strokeWidth={2} aria-hidden="true" />
        {unread && <span className="notification-dot" aria-hidden="true" />}
      </div>

      <div className="notification-content">
        <div className="notification-item-top">
          <p className="notification-item-title">
            {unread && <span className="nd-sr-only">{isAr ? "غير مقروء: " : "Unread: "}</span>}
            {notification.isPersonal && (
              <span
                className={`notification-personal-badge${unread ? "" : " is-read"}`}
                title={isAr ? "شخصي" : "Personal"}
                aria-label={isAr ? "شخصي" : "Personal"}
              >
                <User size={11} strokeWidth={2.75} aria-hidden="true" />
                {unread && (isAr ? "شخصي" : "Personal")}
              </span>
            )}
            {title}
          </p>
          <div className="notification-trailing">
            {timeLabel && (
              <time
                className="notification-time"
                dateTime={date ? date.toISOString() : undefined}
                title={exactDateTime}
              >
                {timeLabel}
              </time>
            )}
            <button
              type="button"
              className="notification-delete-btn"
              onClick={handleDelete}
              title={deleteLabel}
              aria-label={deleteLabel}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
        {showMessage && (
          <p className="notification-item-desc">{notification.message}</p>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
